import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useAuth } from 'wasp/client/auth'
import { useQuery, useAction } from 'wasp/client/operations'
import { getMealPlanById, reorderPlanDays, getShoppingListWithItems, getRecipe, toggleFavorite } from 'wasp/client/operations'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card'
import { Button } from '../../components/ui/button'
import { Badge } from '../../components/ui/badge'
import { ImageWithFallback } from '../../components/figma/ImageWithFallback'
import { Clock, GripVertical, ShoppingCart, Heart, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react'
import { getRecipeImageUrl } from '../../utils/recipeImage'
import { trackEvent } from '../../config/analytics'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog'
import { ScrollArea, ScrollBar } from '../../components/ui/scroll-area'
import { UnifiedRecipeCard } from '../../components/recipe/UnifiedRecipeCard'
import { cn } from '../../components/ui/utils'
import { toast } from 'sonner'
// AppFrame removed: route-level layout wraps pages

/**
 * PlanDetail renders a single meal plan timeline + shopping utilities.
 * - Handles mixed recipe shapes (DB, Spoonacular, fallback) for images/ingredients.
 * - Integrates persisted ShoppingList data with a "Generate" fallback.
 * - Provides a modal View experience for each recipe card.
 */
export function PlanDetail() {
  const { planId } = useParams()
  const { data: user } = useAuth()
  const isAuthenticated = !!user
  const { data: plan, isLoading, error, refetch } = useQuery(getMealPlanById, { id: planId } as any, { enabled: isAuthenticated && !!planId })
  // Persist day reordering
  const doReorder = useAction(reorderPlanDays as any)
  const doToggleFavorite = useAction(toggleFavorite as any)
  const [dragIndex, setDragIndex] = useState<number | null>(null)
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)
  const [daysLocal, setDaysLocal] = useState<any[]>([])
  // View modal state MUST be declared before any early returns to keep hooks ordering stable
  const [open, setOpen] = useState(false)
  const [current, setCurrent] = useState<any | null>(null)
  const [resolvedRecipes, setResolvedRecipes] = useState<Record<string, any>>({})
  const [isFavorited, setIsFavorited] = useState(false)
  // Shopping list state
  const [shoppingListExpanded, setShoppingListExpanded] = useState(false)
  // Swap recipe state
  const [swapModalOpen, setSwapModalOpen] = useState(false)
  const [swapTargetSlot, setSwapTargetSlot] = useState<{ dayIndex: number; mealType: string } | null>(null)
  useEffect(() => { setDaysLocal(Array.isArray((plan as any)?.planData?.days) ? (plan as any).planData.days : []) }, [plan])

  // Resolve missing recipe data from DB (works for quick-mode and hybrid objects lacking ingredients)
  useEffect(() => {
    if (!Array.isArray(daysLocal) || daysLocal.length === 0) return
    const ids = new Set<string>()
    for (const day of daysLocal) {
      const meals = day?.meals
      if (Array.isArray(meals)) {
        for (const m of meals) {
          const id = m?.recipeId && String(m.recipeId)
          if (id && !resolvedRecipes[id]) ids.add(id)
        }
      } else if (meals && typeof meals === 'object') {
        for (const v of Object.values(meals)) {
          const maybe = v as any
          // If the embedded recipe object lacks ingredients (or has minimal fields), fetch the full record by id/slug
          const needsHydrate = !!maybe && (!Array.isArray(maybe.ingredients) || maybe.ingredients.length === 0)
          const id = (maybe?.recipeId || maybe?.id) && String(maybe.recipeId || maybe.id)
          if (needsHydrate && id && !resolvedRecipes[id]) ids.add(id)
        }
      }
    }
    if (ids.size === 0) return
    let cancelled = false
    ;(async () => {
      const entries = await Promise.all(
        Array.from(ids).map(async (id) => {
          try { const r = await getRecipe({ slug: id } as any); return [id, r] as const } catch { return [id, null] as const }
        })
      )
      if (cancelled) return
      setResolvedRecipes((prev) => {
        const next = { ...prev }
        for (const [id, r] of entries) if (r) next[id] = r
        return next
      })
    })()
    return () => { cancelled = true }
  }, [daysLocal])

  // Server shopping list (persisted). If empty, provide a generate button.
  const { data: shoppingList, refetch: refetchShopping } = useQuery(
    getShoppingListWithItems as any,
    { mealPlanId: (plan as any)?.id },
    { enabled: isAuthenticated && !!(plan as any)?.id }
  ) as { data: any | undefined; refetch: () => void }

  const generateShoppingList = async () => {
    if (!(plan as any)?.id) return
    try {
      const res = await fetch('/operations/generate-shopping-list', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mealPlanId: (plan as any).id })
      })
      if (!res.ok) throw new Error(`Failed (${res.status})`)
      await refetchShopping()
    } catch (e) {
      console.error('generateShoppingList failed', e)
    }
  }

  const onDragStart = (idx: number) => () => setDragIndex(idx)
  const onDragOver = (e: React.DragEvent) => { e.preventDefault() }
  const onDragEnter = (idx: number) => (e: React.DragEvent) => { e.preventDefault(); setDragOverIndex(idx) }
  const onDragLeave = (e: React.DragEvent) => { e.preventDefault(); setDragOverIndex(null) }
  const onDrop = (idx: number) => async (e: React.DragEvent) => {
    e.preventDefault()
    if (dragIndex === null || dragIndex === idx) return
    const next = daysLocal.slice()
    const [moved] = next.splice(dragIndex, 1)
    next.splice(idx, 0, moved)
    setDaysLocal(next)
    setDragIndex(null)
    setDragOverIndex(null)
    try {
      const order = next.map(d => String(d?.dateISO || d?.date))
      await doReorder({ mealPlanId: (plan as any).id, dayOrder: order } as any)
      await refetch()
    } catch (e) { console.error('Reorder failed', e) }
  }
  
  // Track open on mount/plan change
  useEffect(() => {
    if (planId) trackEvent('plan_open', { planId })
  }, [planId])

  if (isLoading) return <div className="p-6">Loading plan…</div>
  if (error) return <div className="p-6 text-red-600">Error loading plan.</div>
  if (!plan) return <div className="p-6 text-muted-foreground">No plan found.</div>

  const days: any[] = daysLocal

  // Return a list of meal slots for rendering (works for quick-mode and hybrid/standard)
  function getMealSlots(day: any): Array<{ mealType: string; recipe: any }> {
    if (!day) return []
    if (Array.isArray(day?.meals)) {
      // quick mode shape: array of { type, recipeId }
      return (day.meals as any[]).map((m) => {
        const rid = m?.recipeId && String(m.recipeId)
        const resolved = rid ? resolvedRecipes[rid] : null
        return { mealType: m?.type || 'dinner', recipe: resolved }
      }).filter((s) => s.recipe)
    }
    const meals = day?.meals || {}
    const out: Array<{ mealType: string; recipe: any }> = []
    for (const t of ['breakfast', 'lunch', 'dinner', 'snack']) {
      const r = (meals as any)[t]
      if (r) out.push({ mealType: t, recipe: r })
    }
    // fallback: single recipe day.recipe
    if (!out.length && day?.recipe) out.push({ mealType: 'dinner', recipe: day.recipe })
    return out
  }

  const openRecipe = (r: any) => { setCurrent(r); setOpen(true) }
  const closeRecipe = () => { setOpen(false); setCurrent(null) }

  function mealTypeLabel(t?: string) {
    switch ((t || '').toLowerCase()) {
      case 'breakfast': return 'Breakfast'
      case 'lunch': return 'Lunch'
      case 'dinner': return 'Dinner'
      case 'snack': return 'Dessert'
      default: return 'Meal'
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">Meal Plan</h1>
        <p className="text-sm text-muted-foreground">Week of {new Date((plan as any).weekStartDate).toLocaleDateString()}</p>
      </div>

      {!days.length && (
        <Card className="bg-white border-0 shadow-cardShadow">
          <CardContent className="p-6 text-muted-foreground">No meals in this plan.</CardContent>
        </Card>
      )}

      {!!days.length && (
        <div className="grid gap-4">
          {days.map((d: any, idx: number) => {
            const slots = getMealSlots(d)
            const dayLabel = d?.dateISO ? new Date(d.dateISO).toLocaleDateString(undefined, { weekday: 'long' }) : `Day ${idx+1}`
            if (!slots.length) return (
              <Card key={idx} className={cn(
                'bg-white border shadow-cardShadow transition-all duration-200',
                dragOverIndex === idx && 'border-coral ring-2 ring-coral/30 scale-[1.02]',
                dragIndex === idx && 'opacity-50 scale-95',
                dragIndex !== idx && dragOverIndex !== idx && 'border-stone/20'
              )}
                draggable
                onDragStart={onDragStart(idx)}
                onDragOver={onDragOver}
                onDragEnter={onDragEnter(idx)}
                onDragLeave={onDragLeave}
                onDrop={onDrop(idx)}
              >
                <CardHeader className="pb-3 group">
                  <CardTitle className="text-base flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <GripVertical className={cn(
                        "w-5 h-5 transition-all cursor-grab active:cursor-grabbing",
                        dragIndex === idx
                          ? "text-coral scale-110"
                          : "text-coral/60 group-hover:text-coral group-hover:scale-110"
                      )}
                        aria-label="Drag to reorder"
                      />
                      <span className="font-semibold text-dusk">{dayLabel}</span>
                    </span>
                    <span className="text-sm text-martinique/60">No recipes</span>
                  </CardTitle>
                </CardHeader>
              </Card>
            )

            return (
              <Card key={idx} className={cn(
                'bg-white border shadow-cardShadow transition-all duration-200',
                dragOverIndex === idx && 'border-coral ring-2 ring-coral/30 scale-[1.02]',
                dragIndex === idx && 'opacity-50 scale-95',
                dragIndex !== idx && dragOverIndex !== idx && 'border-stone/20'
              )}
                draggable
                onDragStart={onDragStart(idx)}
                onDragOver={onDragOver}
                onDragEnter={onDragEnter(idx)}
                onDragLeave={onDragLeave}
                onDrop={onDrop(idx)}
              >
                <CardHeader className="pb-3 group">
                  <CardTitle className="text-base flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <GripVertical className={cn(
                        "w-5 h-5 transition-all cursor-grab active:cursor-grabbing",
                        dragIndex === idx
                          ? "text-coral scale-110"
                          : "text-coral/60 group-hover:text-coral group-hover:scale-110"
                      )}
                        aria-label="Drag to reorder"
                      />
                      <span className="font-semibold text-dusk">{dayLabel}</span>
                    </span>
                    <Badge variant="outline" className="bg-coral/10 text-coral border-coral/20">
                      {slots.length} {slots.length === 1 ? 'meal' : 'meals'}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Horizontal scrollable recipe cards */}
                  <ScrollArea className="w-full whitespace-nowrap rounded-md">
                    <div className="flex gap-4 pb-2">
                      {slots.map(({ mealType, recipe }, i) => {
                        const handleSwap = () => {
                          setSwapTargetSlot({ dayIndex: idx, mealType })
                          setSwapModalOpen(true)
                        }

                        return (
                          <div key={`${idx}-${i}`} className="w-[280px] flex-shrink-0">
                            {/* Meal type badge */}
                            <Badge className="mb-2 bg-sage/10 text-sage-800 border-sage-200">
                              {mealTypeLabel(mealType)}
                            </Badge>

                            {/* Recipe card */}
                            <UnifiedRecipeCard
                              recipe={recipe}
                              variant="compact"
                              size="md"
                              features={{
                                like: true,
                                addToPlan: false,
                                rating: true,
                                difficulty: true,
                                tags: false,
                              }}
                              onViewDetails={() => openRecipe(recipe)}
                              imageFetchPriority="auto"
                              showDescription={false}
                              reserveDescription={false}
                            />

                            {/* Swap button below card */}
                            <Button
                              variant="outline"
                              size="sm"
                              className="mt-2 w-full border-coral/20 text-coral hover:bg-coral/10"
                              onClick={handleSwap}
                            >
                              <RefreshCw className="mr-2 h-3.5 w-3.5" />
                              Swap Recipe
                            </Button>
                          </div>
                        )
                      })}
                    </div>
                    <ScrollBar orientation="horizontal" />
                  </ScrollArea>
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}

      {/* Sticky Shopping List Card */}
      {!!days.length && (() => {
        const isQuickPlan = days.length === 1
        const hasShoppingList = Array.isArray(shoppingList?.items) && shoppingList.items.length > 0

        return (
          <Card className={cn(
            "bg-white border shadow-cardShadow transition-all",
            "sticky top-4 z-10",
            shoppingListExpanded ? "border-sage/30" : "border-stone/20"
          )}>
            <CardHeader
              className="pb-3 cursor-pointer select-none"
              onClick={() => setShoppingListExpanded(!shoppingListExpanded)}
            >
              <CardTitle className="flex items-center justify-between text-base">
                <span className="flex items-center gap-2">
                  <ShoppingCart className="w-5 h-5 text-sage" />
                  <span className="font-semibold text-dusk">
                    {isQuickPlan ? 'Shopping List by Meal' : 'Weekly Shopping List'}
                  </span>
                </span>
                <div className="flex items-center gap-2">
                  {hasShoppingList && (
                    <Badge variant="outline" className="bg-sage/10 text-sage-800 border-sage-200">
                      {shoppingList.items.length} items
                    </Badge>
                  )}
                  {shoppingListExpanded ? (
                    <ChevronUp className="w-4 h-4 text-martinique/60" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-martinique/60" />
                  )}
                </div>
              </CardTitle>
            </CardHeader>

            {shoppingListExpanded && (
              <CardContent className="space-y-4">
                {!hasShoppingList ? (
                  <div className="flex flex-col items-center gap-3 py-4">
                    <p className="text-sm text-martinique/60 text-center">
                      Generate a comprehensive shopping list from your meal plan
                    </p>
                    <Button
                      size="sm"
                      className="bg-sage text-white hover:bg-sage-700"
                      onClick={generateShoppingList}
                    >
                      <ShoppingCart className="mr-2 h-4 w-4" />
                      Generate Shopping List
                    </Button>
                  </div>
                ) : isQuickPlan ? (
                  // Quick Plan: Per-Meal Breakdown
                  <div className="space-y-4">
                    {days[0] && getMealSlots(days[0]).map(({ mealType, recipe }) => {
                      // Extract ingredients for this specific meal
                      const mealIngredients = recipe?.ingredients || []
                      const ingredientNames = mealIngredients.map((i: any) => {
                        if (typeof i === 'string') return i
                        return i?.name || i?.ingredient?.name || i?.ingredientName || ''
                      }).filter(Boolean).slice(0, 8)

                      return (
                        <div key={mealType} className="space-y-2">
                          <div className="flex items-center gap-2">
                            <Badge className="bg-coral/10 text-coral-800 border-coral-200">
                              {mealTypeLabel(mealType)}
                            </Badge>
                            <span className="text-xs text-martinique/60">{ingredientNames.length} ingredients</span>
                          </div>
                          <div className="flex flex-wrap gap-2 pl-3">
                            {ingredientNames.map((name: string, i: number) => (
                              <span key={i} className="px-2 py-1 text-xs bg-cream rounded-md border border-stone/20 text-martinique">
                                {name}
                              </span>
                            ))}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  // Regular Plan: Week-Level Rollup
                  <div className="flex flex-wrap gap-2">
                    {shoppingList.items.map((it: any) => (
                      <span key={it.id} className="px-2 py-1 text-xs bg-cream rounded-md border border-stone/20 text-martinique">
                        {it.ingredient?.name || 'item'}
                        {(() => {
                          const q = (it.displayQty ?? it.quantity) as number | undefined
                          const u = (it.displayUnit || '').trim()
                          if (!q && !u) return null
                          return <span className="text-martinique/60"> {q ? `${q}` : ''}{u ? ` ${u}` : ''}</span>
                        })()}
                      </span>
                    ))}
                  </div>
                )}
              </CardContent>
            )}
          </Card>
        )
      })()}

      {/* Recipe details modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-start justify-between gap-4">
              <DialogTitle className="flex-1 text-xl font-semibold text-dusk">
                {current?.title || 'Recipe'}
              </DialogTitle>
              <Button
                variant="outline"
                size="sm"
                className={cn(
                  "flex-shrink-0 border-coral/20",
                  isFavorited
                    ? "bg-coral/10 text-coral hover:bg-coral/20"
                    : "text-coral hover:bg-coral/10"
                )}
                onClick={async () => {
                  if (!current) return
                  try {
                    const recipeId = current.id || current.recipeId || current.slug
                    const isExternal = /^\d+$/.test(String(recipeId))
                    const result = await doToggleFavorite({ recipeId: String(recipeId), isExternal }) as { isFavorited: boolean; recipeId: string }
                    setIsFavorited(result.isFavorited)
                    toast.success(result.isFavorited ? 'Added to favorites!' : 'Removed from favorites')
                  } catch (e) {
                    console.error('Toggle favorite failed:', e)
                    toast.error('Failed to update favorites')
                  }
                }}
              >
                <Heart className={cn("mr-2 h-4 w-4", isFavorited && "fill-coral")} />
                {isFavorited ? 'Saved' : 'Save'}
              </Button>
            </div>
          </DialogHeader>

          {/* Hero Image */}
          <div className="w-full">
            <ImageWithFallback
              src={current?.imageUrl || current?.heroImageUrl || getRecipeImageUrl(current?.title || 'recipe')}
              alt={current?.title || 'Recipe'}
              className="w-full h-64 rounded-lg object-cover"
            />
          </div>

          {/* Quick Info Bar */}
          <div className="grid grid-cols-3 gap-3 py-3 px-4 bg-cream rounded-lg">
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 text-sm font-semibold text-coral">
                <Clock className="h-3.5 w-3.5" />
                {current?.totalMinutes || current?.prepTime || 20} min
              </div>
              <div className="text-xs text-martinique/60">Cook Time</div>
            </div>
            <div className="text-center">
              <div className="text-sm font-semibold text-coral">
                {(() => {
                  const cals = current?.nutritionInfo?.calories ?? current?.calories
                  return cals ? Math.round(cals) : '—'
                })()}
              </div>
              <div className="text-xs text-martinique/60">Calories</div>
            </div>
            <div className="text-center">
              <div className="text-sm font-semibold text-coral">
                {current?.servings || 4}
              </div>
              <div className="text-xs text-martinique/60">Servings</div>
            </div>
          </div>

          {/* Nutrition Facts Panel */}
          {(() => {
            const nutrition = current?.nutritionInfo || {}
            const hasNutrition = nutrition.protein || nutrition.carbs || nutrition.fat
            if (!hasNutrition) return null

            return (
              <Card className="bg-sage/5 border-sage/20">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold text-dusk">Nutrition Facts</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4">
                    {nutrition.protein && (
                      <div className="text-center">
                        <div className="text-lg font-semibold text-sage-700">{Math.round(nutrition.protein)}g</div>
                        <div className="text-xs text-martinique/60">Protein</div>
                      </div>
                    )}
                    {nutrition.carbs && (
                      <div className="text-center">
                        <div className="text-lg font-semibold text-sage-700">{Math.round(nutrition.carbs)}g</div>
                        <div className="text-xs text-martinique/60">Carbs</div>
                      </div>
                    )}
                    {nutrition.fat && (
                      <div className="text-center">
                        <div className="text-lg font-semibold text-sage-700">{Math.round(nutrition.fat)}g</div>
                        <div className="text-xs text-martinique/60">Fat</div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )
          })()}

          {/* Ingredients */}
          <div className="space-y-2">
            <h3 className="font-semibold text-base text-dusk">Ingredients</h3>
            <ul className="space-y-1.5">
              {(Array.isArray(current?.ingredients) ? current?.ingredients : []).slice(0, 24).map((i: any, idx: number) => {
                let name = ''
                let qty: number | string | undefined
                let unit: string | undefined
                if (typeof i === 'string') name = i
                else {
                  name = i?.name || i?.ingredient?.name || i?.ingredientName || (i?.ingredientId ? String(i.ingredientId).replace(/[-_]/g, ' ') : '')
                  qty = i?.displayQty ?? i?.quantity ?? i?.amount
                  unit = i?.displayUnit || i?.unit
                }
                if (!name) return null
                return (
                  <li key={idx} className="flex items-baseline gap-2 text-sm">
                    <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-coral mt-1.5" />
                    <span className="flex-1">
                      {name}
                      {qty || unit ? (
                        <span className="text-martinique/60 ml-2">
                          {qty ?? ''}{unit ? ` ${unit}` : ''}
                        </span>
                      ) : null}
                    </span>
                  </li>
                )
              })}
            </ul>
          </div>

          {/* Instructions */}
          {Array.isArray(current?.instructions) && current.instructions.length > 0 && (
            <div className="space-y-2">
              <h3 className="font-semibold text-base text-dusk">Instructions</h3>
              <ol className="space-y-3">
                {current.instructions.slice(0, 20).map((s: string, i: number) => (
                  <li key={i} className="flex gap-3 text-sm">
                    <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-coral/10 text-coral text-xs font-semibold">
                      {i + 1}
                    </span>
                    <span className="flex-1 pt-0.5 text-martinique">{s}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
